import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  History,
  Save,
} from "lucide-react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import type {
  AcademicBaseGrade,
  AcademicGradeEntryMutationEntry,
  AcademicGradeEntryStudent,
  AcademicGradeEntryWorkspace,
  AcademicGradeModifier,
} from "@triedge/academics-domain";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  buildWorkspaceWithDraftEntries,
  createDraftEntriesFromWorkspace,
  diffGradeEntryDraftEntries,
  getDefaultExpandedStudentKey,
  SOURCE_FAITHFUL_GRADE_ENTRY_QUERY,
} from "./grade-entry-view-model";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
} from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

const buildAcademicsSectionItems = (
  workspace?: AcademicGradeEntryWorkspace | null,
  reportCardStudentKey?: string | null,
): ModuleSectionNavItem[] => {
  const resolvedReportCardStudentKey =
    reportCardStudentKey ?? workspace?.students[0]?.studentKey ?? null;

  return [
    {
      id: "grade-entry",
      label: "Grade Entry",
      to: "/school/academics",
      end: true,
    },
    { id: "settings", label: "Settings", to: "/school/academics/settings" },
    { id: "approvals", label: "Approvals", to: "/school/academics/approvals" },
    { id: "summary", label: "Summary", to: "/school/academics/summary" },
    {
      id: "report-card",
      label: "Report Card",
      to: resolvedReportCardStudentKey
        ? `/school/academics/report-cards/${resolvedReportCardStudentKey}`
        : "/school/academics",
      disabled: !resolvedReportCardStudentKey,
      match: (pathname) =>
        pathname.startsWith("/school/academics/report-cards/"),
    },
  ];
};

const GRADE_BUTTONS: readonly AcademicBaseGrade[] = ["A", "B", "C", "D", "F"];
const MODIFIER_BUTTONS: readonly AcademicGradeModifier[] = ["+", "none", "-"];

const gradeAccent = {
  A: {
    pill: "border-teal-300 bg-teal-50 text-teal-700",
    selected:
      "border-teal-500 bg-teal-600 text-white shadow-[0_10px_24px_rgba(20,184,166,0.22)]",
    subtle: "bg-teal-50 text-teal-700",
  },
  B: {
    pill: "border-sky-300 bg-sky-50 text-sky-700",
    selected:
      "border-sky-500 bg-sky-600 text-white shadow-[0_10px_24px_rgba(14,165,233,0.22)]",
    subtle: "bg-sky-50 text-sky-700",
  },
  C: {
    pill: "border-rose-300 bg-rose-50 text-rose-700",
    selected:
      "border-rose-500 bg-rose-500 text-white shadow-[0_10px_24px_rgba(244,63,94,0.22)]",
    subtle: "bg-rose-50 text-rose-700",
  },
  D: {
    pill: "border-amber-300 bg-amber-50 text-amber-700",
    selected:
      "border-amber-500 bg-amber-500 text-white shadow-[0_10px_24px_rgba(245,158,11,0.22)]",
    subtle: "bg-amber-50 text-amber-700",
  },
  F: {
    pill: "border-red-300 bg-red-50 text-red-700",
    selected:
      "border-red-500 bg-red-600 text-white shadow-[0_10px_24px_rgba(239,68,68,0.22)]",
    subtle: "bg-red-50 text-red-700",
  },
  ungraded: {
    pill: "border-amber-200 bg-amber-50 text-amber-700",
    selected:
      "border-amber-500 bg-amber-500 text-white shadow-[0_10px_24px_rgba(245,158,11,0.22)]",
    subtle: "bg-amber-50 text-amber-700",
  },
} as const;

const statusCardStyles = {
  normal:
    "border border-slate-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.05)]",
  "needs-attention":
    "border border-amber-200 bg-[linear-gradient(135deg,rgba(254,243,199,0.72),rgba(255,255,255,0.98))] shadow-[0_14px_34px_rgba(245,158,11,0.10)]",
  "below-passing":
    "border border-rose-200 bg-white shadow-[0_14px_36px_rgba(244,63,94,0.09)]",
} as const;

const statusStripeStyles = {
  normal: "bg-teal-500/0",
  "needs-attention": "bg-amber-400",
  "below-passing": "bg-rose-500",
} as const;

const formatTimestamp = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const Shell = ({
  title,
  eyebrow,
  children,
  testId,
  sectionItems,
  activeSectionId,
}: {
  title: string;
  eyebrow: string;
  children: ReactNode;
  testId: string;
  sectionItems: ModuleSectionNavItem[];
  activeSectionId?: string;
}) => (
  <main
    data-testid={testId}
    data-extracted-from="source-faithful:school-grade-entry-spreadsheet-v1.0"
    data-route-family="source-faithful"
    data-route-ready="true"
    className="source-faithful-shell"
    style={{ "--ystnj-source-faithful-max-width": "1040px" } as CSSProperties}
  >
    <YstRouteTransition className="source-faithful-container">
      <header>
        <p className="yst-surface-eyebrow !text-text-secondary">{eyebrow}</p>
        <h2 className="yst-surface-title">{title}</h2>
      </header>
      <ModuleSectionNav
        items={sectionItems}
        activeId={activeSectionId}
        ariaLabel="Academics sections"
        desktopClassName="source-faithful-tabs"
        testIds={{
          root: "academics-section-nav",
          itemPrefix: "academics-section-nav-item",
          mobileTrigger: "academics-section-nav-mobile-trigger",
          mobileDialog: "academics-section-nav-mobile-dialog",
          mobileItemPrefix: "academics-section-nav-mobile-item",
        }}
      />
      {children}
    </YstRouteTransition>
  </main>
);

const ProgressIndicator = ({
  workspace,
}: {
  workspace: AcademicGradeEntryWorkspace;
}) => (
  <section
    className="rounded-[24px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(247,250,252,0.98))] px-5 py-5 shadow-[0_18px_48px_rgba(15,23,42,0.06)]"
    data-testid="academics-grade-entry-progress"
  >
    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
          Class progress
        </p>
        <p className="mt-1 text-sm font-medium text-[var(--iron-grey)]">
          {workspace.progress.gradedStudents}/{workspace.progress.totalStudents}{" "}
          Students graded
        </p>
      </div>
      <div className="text-right">
        <p className="text-2xl font-semibold text-[var(--iron-grey)]">
          {workspace.progress.percentComplete}%
        </p>
        <p className="text-xs text-[var(--text-secondary)]">
          {workspace.workflowState === "submitted"
            ? "Submitted"
            : `${workspace.progress.ungradedStudents} remaining`}
        </p>
      </div>
    </div>
    <div className="h-3 rounded-full bg-slate-100">
      <div
        className="h-full rounded-full bg-[linear-gradient(90deg,var(--aquamarine),var(--primary))] transition-[width] duration-500"
        style={{ width: `${workspace.progress.percentComplete}%` }}
      />
    </div>
  </section>
);

const LoadingStateCard = ({
  title,
  message,
  testId,
}: {
  title: string;
  message: string;
  testId: string;
}) => (
  <section
    data-testid={testId}
    className="rounded-[24px] border border-slate-200 bg-white px-6 py-8 shadow-[0_18px_48px_rgba(15,23,42,0.06)]"
  >
    <p className="text-sm font-semibold text-[var(--iron-grey)]">{title}</p>
    <p className="mt-2 text-sm text-[var(--text-secondary)]">{message}</p>
  </section>
);

const UnavailableStateCard = ({
  title,
  message,
  testId,
}: {
  title: string;
  message: string;
  testId: string;
}) => (
  <section
    data-testid={testId}
    className="rounded-[24px] border border-amber-200 bg-[linear-gradient(135deg,rgba(255,251,235,0.96),rgba(255,255,255,0.98))] px-6 py-8 shadow-[0_18px_48px_rgba(15,23,42,0.06)]"
  >
    <div className="flex items-start gap-3">
      <div className="rounded-full bg-amber-100 p-2 text-amber-700">
        <AlertCircle size={18} />
      </div>
      <div>
        <p className="text-sm font-semibold text-[var(--iron-grey)]">{title}</p>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{message}</p>
      </div>
    </div>
  </section>
);

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.trim()
  ) {
    return error.message;
  }
  return fallback;
};

const isBelowTarget = (
  student: Pick<AcademicGradeEntryStudent, "baseGrade">,
): boolean =>
  student.baseGrade === "C" ||
  student.baseGrade === "D" ||
  student.baseGrade === "F";

const countMissingInterventionComments = (
  students: readonly Pick<
    AcademicGradeEntryStudent,
    "baseGrade" | "hasComment"
  >[],
): number =>
  students.filter((student) => isBelowTarget(student) && !student.hasComment)
    .length;

const toTitleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const GradePill = ({ grade }: { grade: string | null }) => {
  const accent =
    grade && gradeAccent[grade.charAt(0) as keyof typeof gradeAccent]
      ? gradeAccent[grade.charAt(0) as keyof typeof gradeAccent]
      : gradeAccent.ungraded;
  return (
    <span
      className={`inline-flex min-w-12 items-center justify-center rounded-full border px-3 py-1 text-sm font-semibold ${accent.pill}`}
    >
      {grade ?? "—"}
    </span>
  );
};

const GradeSegmentButton = ({
  selected,
  tone,
  label,
  onClick,
  disabled,
  testId,
}: {
  selected: boolean;
  tone: keyof typeof gradeAccent;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) => {
  const accent = gradeAccent[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={[
        "inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border px-4 text-sm font-semibold transition-all",
        selected
          ? accent.selected
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
        disabled ? "cursor-not-allowed opacity-50" : "",
      ].join(" ")}
    >
      {label}
    </button>
  );
};

const GradeEntryStudentCard = ({
  student,
  expanded,
  readOnly,
  onToggle,
  onBaseGradeChange,
  onModifierChange,
  onCommentChange,
}: {
  student: AcademicGradeEntryStudent;
  expanded: boolean;
  readOnly: boolean;
  onToggle: () => void;
  onBaseGradeChange: (grade: AcademicBaseGrade) => void;
  onModifierChange: (modifier: AcademicGradeModifier) => void;
  onCommentChange: (value: string) => void;
}) => {
  const tone = (student.baseGrade ?? "ungraded") as keyof typeof gradeAccent;
  const historyEmpty = student.history.length === 0;

  return (
    <article
      className={`overflow-hidden rounded-[26px] ${statusCardStyles[student.status]}`}
      data-testid={`academics-grade-entry-card-${student.studentKey}`}
    >
      <div className="flex items-stretch">
        <div
          className={`w-1.5 shrink-0 ${statusStripeStyles[student.status]}`}
          aria-hidden="true"
        />
        <div className="flex-1">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            data-testid={`academics-grade-entry-toggle-${student.studentKey}`}
            className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-slate-50/70"
          >
            <div className="min-w-0">
              <p className="text-base font-semibold text-[var(--iron-grey)]">
                {student.name}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
                {student.hasComment ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-2.5 py-1 shadow-sm">
                    <FileText size={13} className="text-[var(--primary)]" />
                    Comment ready
                  </span>
                ) : null}
                {student.status === "needs-attention" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-amber-800">
                    <AlertCircle size={13} />
                    Needs teacher action
                  </span>
                ) : null}
                {student.status === "below-passing" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-rose-700">
                    <AlertCircle size={13} />
                    Intervention watch
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <GradePill grade={student.grade} />
              {expanded ? (
                <ChevronUp size={18} className="text-[var(--text-secondary)]" />
              ) : (
                <ChevronDown
                  size={18}
                  className="text-[var(--text-secondary)]"
                />
              )}
            </div>
          </button>

          {expanded ? (
            <div
              className="border-t border-slate-200 bg-[linear-gradient(180deg,rgba(250,252,255,0.96),rgba(255,255,255,0.98))] px-5 py-5"
              data-testid={`academics-grade-entry-panel-${student.studentKey}`}
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
                <section className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
                  <div className="grid gap-5">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
                        Select grade
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {GRADE_BUTTONS.map((grade) => (
                          <GradeSegmentButton
                            key={grade}
                            label={grade}
                            tone={grade}
                            selected={student.baseGrade === grade}
                            disabled={readOnly}
                            onClick={() => onBaseGradeChange(grade)}
                            testId={`academics-grade-entry-base-${student.studentKey}-${grade}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
                        Modifier
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {MODIFIER_BUTTONS.map((modifier) => (
                          <GradeSegmentButton
                            key={modifier}
                            label={modifier === "none" ? "None" : modifier}
                            tone={tone}
                            selected={
                              (student.baseGrade === "F"
                                ? "none"
                                : student.modifier) === modifier
                            }
                            disabled={
                              readOnly ||
                              !student.baseGrade ||
                              student.baseGrade === "F"
                            }
                            onClick={() => onModifierChange(modifier)}
                            testId={`academics-grade-entry-modifier-${student.studentKey}-${modifier}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
                          Teacher comment
                        </p>
                        {student.hasComment ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${gradeAccent[tone].subtle}`}
                          >
                            <FileText size={12} className="mr-1" />
                            Comment saved
                          </span>
                        ) : null}
                      </div>
                      <textarea
                        value={student.comment ?? ""}
                        onChange={(event) =>
                          onCommentChange(event.currentTarget.value)
                        }
                        disabled={readOnly}
                        rows={4}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-[var(--primary)] focus:bg-white"
                        placeholder="Add a short note for report-card context or intervention follow-up."
                        data-testid={`academics-grade-entry-comment-${student.studentKey}`}
                      />
                    </div>
                  </div>
                </section>

                <section
                  className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-[0_12px_28px_rgba(15,23,42,0.04)]"
                  data-testid={`academics-grade-entry-history-${student.studentKey}`}
                >
                  <div className="mb-4 flex items-center gap-2">
                    <History size={16} className="text-[var(--primary)]" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
                        Historical reference
                      </p>
                      <p className="text-sm text-slate-600">
                        Quick context while entering this term’s grade.
                      </p>
                    </div>
                  </div>
                  {historyEmpty ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                      No prior submitted grade reference is available for this
                      student yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {student.history.map((reference) => (
                        <div
                          key={reference.id}
                          className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                                {reference.label}
                              </p>
                              {reference.note ? (
                                <p className="mt-1 text-sm text-slate-600">
                                  {reference.note}
                                </p>
                              ) : null}
                            </div>
                            {reference.grade ? (
                              <span
                                className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 text-sm font-semibold ${gradeAccent[reference.grade.charAt(0) as keyof typeof gradeAccent]?.subtle ?? gradeAccent.ungraded.subtle}`}
                              >
                                {reference.grade}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

const useSourceFaithfulAcademicsWorkspace = () =>
  useDomainRouterQuery(
    "getGradeEntryWorkspace",
    SOURCE_FAITHFUL_GRADE_ENTRY_QUERY,
  );

const GradeEntryPage = () => {
  const query = useSourceFaithfulAcademicsWorkspace();
  const [saveGradeEntryDraft, saveGradeEntryDraftState] =
    useDomainRouterMutation("saveGradeEntryDraft");
  const [submitGradeEntry, submitGradeEntryState] =
    useDomainRouterMutation("submitGradeEntry");
  const [workspace, setWorkspace] =
    useState<AcademicGradeEntryWorkspace | null>(null);
  const [draftEntries, setDraftEntries] = useState<
    AcademicGradeEntryMutationEntry[] | null
  >(null);
  const [expandedStudentKey, setExpandedStudentKey] = useState<string | null>(
    null,
  );
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<
    "success" | "warning" | "error"
  >("success");

  useEffect(() => {
    const data = query.data;
    if (!data) {
      return;
    }
    setWorkspace(data);
    setDraftEntries(createDraftEntriesFromWorkspace(data));
    setExpandedStudentKey((current) =>
      current && data.students.some((student) => student.studentKey === current)
        ? current
        : getDefaultExpandedStudentKey(data),
    );
  }, [query.data]);

  const activeWorkspace = workspace ?? query.data ?? null;
  const activeDraftEntries = useMemo(
    () =>
      draftEntries ??
      (activeWorkspace ? createDraftEntriesFromWorkspace(activeWorkspace) : []),
    [activeWorkspace, draftEntries],
  );
  const draftWorkspace = useMemo(
    () =>
      activeWorkspace
        ? buildWorkspaceWithDraftEntries(activeWorkspace, activeDraftEntries)
        : null,
    [activeWorkspace, activeDraftEntries],
  );
  const dirtyEntries = useMemo(
    () =>
      activeWorkspace
        ? diffGradeEntryDraftEntries(activeWorkspace, activeDraftEntries)
        : [],
    [activeWorkspace, activeDraftEntries],
  );

  const unavailableMessage = useMemo(
    () =>
      getErrorMessage(
        query.error,
        "Academic grade-entry is not configured for this route yet.",
      ),
    [query.error],
  );
  const sectionItems = useMemo(
    () => buildAcademicsSectionItems(activeWorkspace),
    [activeWorkspace],
  );

  const updateDraftEntry = (
    studentKey: string,
    updater: (
      current: AcademicGradeEntryMutationEntry,
    ) => AcademicGradeEntryMutationEntry,
  ) => {
    if (!activeWorkspace) {
      return;
    }
    setDraftEntries((current) =>
      (current ?? createDraftEntriesFromWorkspace(activeWorkspace)).map(
        (entry) => (entry.studentKey === studentKey ? updater(entry) : entry),
      ),
    );
  };

  const handleBaseGradeChange = (
    studentKey: string,
    grade: AcademicBaseGrade,
  ) => {
    updateDraftEntry(studentKey, (entry) => ({
      ...entry,
      baseGrade: grade,
      modifier: grade === "F" ? "none" : (entry.modifier ?? "none"),
    }));
  };

  const handleModifierChange = (
    studentKey: string,
    modifier: AcademicGradeModifier,
  ) => {
    updateDraftEntry(studentKey, (entry) => ({
      ...entry,
      modifier: entry.baseGrade === "F" ? "none" : modifier,
    }));
  };

  const handleCommentChange = (studentKey: string, value: string) => {
    updateDraftEntry(studentKey, (entry) => ({
      ...entry,
      comment: value,
    }));
  };

  const persistWorkspaceLocally = (
    nextWorkspace: AcademicGradeEntryWorkspace,
    message: string,
    tone: "success" | "warning" | "error",
  ) => {
    setWorkspace(nextWorkspace);
    setDraftEntries(createDraftEntriesFromWorkspace(nextWorkspace));
    setFeedbackMessage(message);
    setFeedbackTone(tone);
  };

  const handleSaveDraft = async () => {
    if (!activeWorkspace || !draftWorkspace) {
      return;
    }
    if (dirtyEntries.length === 0) {
      setFeedbackMessage("No draft changes to save.");
      setFeedbackTone("warning");
      return;
    }

    try {
      const result = await saveGradeEntryDraft({
        input: {
          gradebookId: activeWorkspace.gradebookId,
          entries: dirtyEntries,
        },
      });
      persistWorkspaceLocally(result, "Draft saved.", "success");
    } catch {
      setFeedbackMessage("Unable to save the grade draft right now.");
      setFeedbackTone("error");
    }
  };

  const handleSubmitGrades = async () => {
    if (!activeWorkspace || !draftWorkspace) {
      return;
    }
    if (draftWorkspace.progress.ungradedStudents > 0) {
      setFeedbackMessage("Every student needs a grade before submit.");
      setFeedbackTone("warning");
      return;
    }
    if (typeof window !== "undefined") {
      const confirmed = window.confirm(
        "Submit grades and lock this gradebook for this marking period?",
      );
      if (!confirmed) {
        return;
      }
    }

    try {
      const result = await submitGradeEntry({
        input: {
          gradebookId: activeWorkspace.gradebookId,
          entries: createDraftEntriesFromWorkspace(draftWorkspace),
        },
      });
      persistWorkspaceLocally(result, "Grades submitted.", "success");
    } catch {
      setFeedbackMessage("Unable to submit grades right now.");
      setFeedbackTone("error");
    }
  };

  if (query.isLoading && !activeWorkspace) {
    return (
      <Shell
        title="Grade Entry"
        eyebrow="ACADEMICS WORKFLOW"
        testId="academics-source-faithful-root"
        sectionItems={sectionItems}
        activeSectionId="grade-entry"
      >
        <LoadingStateCard
          title="Loading grade-entry workspace"
          message="Fetching the configured roster and current draft grades."
          testId="academics-grade-entry-loading"
        />
      </Shell>
    );
  }

  if (!activeWorkspace || !draftWorkspace) {
    return (
      <Shell
        title="Grade Entry"
        eyebrow="ACADEMICS WORKFLOW"
        testId="academics-source-faithful-root"
        sectionItems={sectionItems}
        activeSectionId="grade-entry"
      >
        <UnavailableStateCard
          title="Grade-entry workspace unavailable"
          message={unavailableMessage}
          testId="academics-grade-entry-unavailable"
        />
      </Shell>
    );
  }

  const isReadOnly = draftWorkspace.workflowState === "submitted";
  const saveDisabled =
    isReadOnly ||
    dirtyEntries.length === 0 ||
    saveGradeEntryDraftState.isLoading ||
    submitGradeEntryState.isLoading;
  const submitDisabled =
    isReadOnly ||
    draftWorkspace.progress.ungradedStudents > 0 ||
    saveGradeEntryDraftState.isLoading ||
    submitGradeEntryState.isLoading;

  return (
    <Shell
      title="Grade Entry"
      eyebrow={`${draftWorkspace.subjectTitle.toUpperCase()} · ${draftWorkspace.classLabel.toUpperCase()} · ${draftWorkspace.periodLabel.toUpperCase()}`}
      testId="academics-source-faithful-root"
      sectionItems={sectionItems}
      activeSectionId="grade-entry"
    >
      <ProgressIndicator workspace={draftWorkspace} />

      <div className="space-y-4">
        {draftWorkspace.students.map((student) => (
          <GradeEntryStudentCard
            key={student.studentKey}
            student={student}
            expanded={expandedStudentKey === student.studentKey}
            readOnly={isReadOnly}
            onToggle={() =>
              setExpandedStudentKey((current) =>
                current === student.studentKey ? null : student.studentKey,
              )
            }
            onBaseGradeChange={(grade) =>
              handleBaseGradeChange(student.studentKey, grade)
            }
            onModifierChange={(modifier) =>
              handleModifierChange(student.studentKey, modifier)
            }
            onCommentChange={(value) =>
              handleCommentChange(student.studentKey, value)
            }
          />
        ))}
      </div>

      <div className="source-faithful-stackActions items-center justify-between rounded-[22px] border border-slate-200 bg-white/90 px-5 py-4 shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
        <div className="min-w-0 text-sm">
          {feedbackMessage && feedbackTone === "error" ? (
            <SupportErrorMessage className="text-sm">
              {feedbackMessage}
            </SupportErrorMessage>
          ) : feedbackMessage ? (
            <p
              className={`inline-flex items-center gap-2 ${feedbackTone === "warning" ? "text-amber-700" : "text-emerald-700"}`}
            >
              <CheckCircle2 size={16} />
              {feedbackMessage}
            </p>
          ) : (
            <p className="text-[var(--text-secondary)]">
              {`Last updated ${formatTimestamp(draftWorkspace.updatedAt) ?? "just now"}.`}
            </p>
          )}
          {draftWorkspace.submittedAt ? (
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              Submitted {formatTimestamp(draftWorkspace.submittedAt)}
            </p>
          ) : null}
        </div>

        <div className="source-faithful-stackActions justify-end">
          <button
            type="button"
            disabled={saveDisabled}
            onClick={() => void handleSaveDraft()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-[var(--iron-grey)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save size={16} />
            {saveGradeEntryDraftState.isLoading ? "Saving…" : "Save Draft"}
          </button>
          <button
            type="button"
            disabled={submitDisabled}
            onClick={() => void handleSubmitGrades()}
            className="rounded-xl bg-[var(--primary)] px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(38,151,162,0.2)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitGradeEntryState.isLoading
              ? "Submitting…"
              : isReadOnly
                ? "Submitted"
                : "Submit Grades"}
          </button>
        </div>
      </div>
    </Shell>
  );
};

const SettingsPage = () => {
  const query = useSourceFaithfulAcademicsWorkspace();
  const workspace = query.data;
  const sectionItems = useMemo(
    () => buildAcademicsSectionItems(workspace),
    [workspace],
  );

  return (
    <Shell
      title="Academic Settings"
      eyebrow={
        workspace
          ? `${workspace.subjectTitle.toUpperCase()} · ${workspace.classLabel.toUpperCase()} · ${workspace.periodLabel.toUpperCase()}`
          : "ACADEMICS WORKFLOW"
      }
      testId="academics-source-faithful-settings"
      sectionItems={sectionItems}
      activeSectionId="settings"
    >
      {query.isLoading && !workspace ? (
        <LoadingStateCard
          title="Loading academic settings"
          message="Fetching the configured gradebook metadata."
          testId="academics-settings-loading"
        />
      ) : !workspace ? (
        <UnavailableStateCard
          title="Academic settings unavailable"
          message={getErrorMessage(
            query.error,
            "Academic settings are not configured for this route yet.",
          )}
          testId="academics-settings-unavailable"
        />
      ) : (
        <div className="source-faithful-splitGrid">
          {[
            ["Marking period", workspace.periodLabel],
            ["Teacher", workspace.teacherName],
            ["Workflow state", toTitleCase(workspace.workflowState)],
            ["Roster size", `${workspace.progress.totalStudents} students`],
          ].map(([label, value]) => (
            <article
              key={label}
              className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                {label}
              </p>
              <p className="mt-3 text-lg font-semibold text-[var(--iron-grey)]">
                {value}
              </p>
            </article>
          ))}
        </div>
      )}
    </Shell>
  );
};

const ApprovalsPage = () => {
  const query = useSourceFaithfulAcademicsWorkspace();
  const workspace = query.data;
  const sectionItems = useMemo(
    () => buildAcademicsSectionItems(workspace),
    [workspace],
  );

  return (
    <Shell
      title="Report Card Approvals"
      eyebrow={
        workspace
          ? `${workspace.subjectTitle.toUpperCase()} · ${workspace.classLabel.toUpperCase()} · ${workspace.periodLabel.toUpperCase()}`
          : "ACADEMICS WORKFLOW"
      }
      testId="academics-source-faithful-approvals"
      sectionItems={sectionItems}
      activeSectionId="approvals"
    >
      {query.isLoading && !workspace ? (
        <LoadingStateCard
          title="Loading approval workflow"
          message="Checking the configured gradebook status."
          testId="academics-approvals-loading"
        />
      ) : !workspace ? (
        <UnavailableStateCard
          title="Approval workflow unavailable"
          message={getErrorMessage(
            query.error,
            "Report-card approval data is not configured for this route yet.",
          )}
          testId="academics-approvals-unavailable"
        />
      ) : (
        <div className="space-y-3">
          <div className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] px-4 py-4 shadow-sm">
            <p className="font-semibold text-[var(--iron-grey)]">
              Current gradebook status
            </p>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {workspace.workflowState === "submitted"
                ? `Submitted ${formatTimestamp(workspace.submittedAt) ?? "recently"}.`
                : `${workspace.progress.ungradedStudents} students still need a final grade before submit.`}
            </p>
          </div>
          <UnavailableStateCard
            title="Approval routing not configured"
            message="This route has a real gradebook workspace, but reviewer assignment and sign-off data have not been configured yet."
            testId="academics-approvals-empty-state"
          />
        </div>
      )}
    </Shell>
  );
};

const SummaryPage = () => {
  const query = useSourceFaithfulAcademicsWorkspace();
  const workspace = query.data;
  const sectionItems = useMemo(
    () => buildAcademicsSectionItems(workspace),
    [workspace],
  );

  return (
    <Shell
      title="Class Summary"
      eyebrow={
        workspace
          ? `${workspace.subjectTitle.toUpperCase()} · ${workspace.classLabel.toUpperCase()} · ${workspace.periodLabel.toUpperCase()}`
          : "ACADEMICS WORKFLOW"
      }
      testId="academics-source-faithful-summary"
      sectionItems={sectionItems}
      activeSectionId="summary"
    >
      {query.isLoading && !workspace ? (
        <LoadingStateCard
          title="Loading class summary"
          message="Compiling the live gradebook summary."
          testId="academics-summary-loading"
        />
      ) : !workspace ? (
        <UnavailableStateCard
          title="Class summary unavailable"
          message={getErrorMessage(
            query.error,
            "Class summary data is not configured for this route yet.",
          )}
          testId="academics-summary-unavailable"
        />
      ) : (
        <div className="source-faithful-kpiGrid">
          {[
            [
              "Students graded",
              `${workspace.progress.gradedStudents}/${workspace.progress.totalStudents}`,
            ],
            [
              "Missing intervention comments",
              `${countMissingInterventionComments(workspace.students)}`,
            ],
            [
              "Students below target",
              `${workspace.students.filter((student) => isBelowTarget(student)).length}`,
            ],
          ].map(([label, value]) => (
            <article
              key={label}
              className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                {label}
              </p>
              <p className="mt-3 text-2xl font-semibold text-[var(--iron-grey)]">
                {value}
              </p>
            </article>
          ))}
        </div>
      )}
    </Shell>
  );
};

const ReportCardPage = () => {
  const query = useSourceFaithfulAcademicsWorkspace();
  const workspace = query.data;
  const { studentId } = useParams<{ studentId: string }>();
  const student =
    workspace?.students.find((entry) => entry.studentKey === studentId) ?? null;
  const sectionItems = useMemo(
    () => buildAcademicsSectionItems(workspace, studentId),
    [studentId, workspace],
  );

  return (
    <Shell
      title="Report Card Preview"
      eyebrow={
        student
          ? `${student.name.toUpperCase()} · ${workspace?.periodLabel.toUpperCase()}`
          : "ACADEMICS WORKFLOW"
      }
      testId="academics-source-faithful-report-card"
      sectionItems={sectionItems}
      activeSectionId="report-card"
    >
      {query.isLoading && !workspace ? (
        <LoadingStateCard
          title="Loading report-card preview"
          message="Fetching the selected student from the configured gradebook."
          testId="academics-report-card-loading"
        />
      ) : !workspace ? (
        <UnavailableStateCard
          title="Report-card preview unavailable"
          message={getErrorMessage(
            query.error,
            "Report-card preview data is not configured for this route yet.",
          )}
          testId="academics-report-card-unavailable"
        />
      ) : !student ? (
        <UnavailableStateCard
          title="Student not found"
          message="This student is not present in the configured grade-entry workspace for the current marking period."
          testId="academics-report-card-student-unavailable"
        />
      ) : (
        <div className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-6 shadow-sm">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-[var(--iron-grey)]">
                {workspace.periodLabel} Summary
              </h2>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                {student.name}
              </p>
            </div>
            <span className="text-3xl font-black text-[var(--primary)]">
              {student.grade ?? "—"}
            </span>
          </div>
          <div className="space-y-3">
            <div className="rounded border border-[var(--border)] bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                Teacher comment
              </p>
              <p className="mt-2 text-sm text-[var(--iron-grey)]">
                {student.comment ?? "No submitted comment yet."}
              </p>
            </div>
            <div className="rounded border border-[var(--border)] bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                Historical reference
              </p>
              {student.history.length === 0 ? (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  No historical reference is available for this student yet.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {student.history.map((reference) => (
                    <div
                      key={reference.id}
                      className="flex items-start justify-between gap-3 rounded border border-slate-100 bg-slate-50 px-3 py-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-[var(--iron-grey)]">
                          {reference.label}
                        </p>
                        {reference.note ? (
                          <p className="mt-1 text-sm text-[var(--text-secondary)]">
                            {reference.note}
                          </p>
                        ) : null}
                      </div>
                      {reference.grade ? (
                        <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-[var(--iron-grey)] shadow-sm">
                          {reference.grade}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
};

export function AcademicsSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<GradeEntryPage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="grade-entry" element={<GradeEntryPage />} />
      <Route path="approvals" element={<ApprovalsPage />} />
      <Route path="summary" element={<SummaryPage />} />
      <Route path="report-cards/:studentId" element={<ReportCardPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
